import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { BootstrapResponse } from "@ppx/shared-types";
import { fetchBootstrap } from "../lib/api";
import { langFromDefault, translate, type Lang, type TranslationKey } from "../lib/i18n";

interface ConfigValue {
  status: "loading" | "ready" | "error";
  bootstrap: BootstrapResponse | null;
  error: string | null;
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
  reload: () => void;
}

const ConfigContext = createContext<ConfigValue | null>(null);

export function ConfigProvider({ children }: { children: ReactNode }): ReactNode {
  const [status, setStatus] = useState<ConfigValue["status"]>("loading");
  const [bootstrap, setBootstrap] = useState<BootstrapResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lang, setLangState] = useState<Lang>("en");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    setError(null);

    void (async () => {
      try {
        const data = await fetchBootstrap();
        if (cancelled) {
          return;
        }
        document.title = data.app.name;
        document.documentElement.lang = data.app.default_lang === "spanish" ? "es" : "en";
        document.documentElement.style.setProperty("--brand-primary", data.brand.primary_color);
        document.documentElement.style.setProperty("--brand-accent", data.brand.accent_color);
        if (data.brand.logo_url !== "") {
          document.documentElement.style.setProperty("--brand-logo", `url("${data.brand.logo_url}")`);
        }
        setLangState(langFromDefault(data.app.default_lang));
        setBootstrap(data);
        setStatus("ready");
      } catch (caught) {
        if (cancelled) {
          return;
        }
        setError(caught instanceof Error ? caught.message : "bootstrap_failed");
        setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const reload = useCallback(() => {
    setAttempt((value) => value + 1);
  }, []);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    document.documentElement.lang = next;
  }, []);

  const t = useCallback<ConfigValue["t"]>(
    (key, params) => translate(lang, key, params),
    [lang],
  );

  const value = useMemo<ConfigValue>(
    () => ({ status, bootstrap, error, lang, setLang, t, reload }),
    [status, bootstrap, error, lang, setLang, t, reload],
  );

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
}

export function useConfig(): ConfigValue {
  const value = useContext(ConfigContext);
  if (value === null) {
    throw new Error("useConfig must be used within ConfigProvider");
  }
  return value;
}
